/**
 * Why a send FAILED — the `sent_emails.failReason` codes and the
 * `emailFailReason` locale key each one renders as.
 *
 * MIRROR of main-consumer/src/lib/email-sent-repo.ts `EmailFailReason` (one
 * code per class of `ses-error-class.ts`, plus `claim-held-unknown`) and of
 * the list on main-backend `EmailSent.failReason`. The consumer is the only
 * writer and stores a code, never SES text. A FAILED row has no SES message
 * id, so no delivery events either: this code is all the detail dialog can
 * say about it (board H-14 — the dialog used to show only "No events").
 *
 * Static on purpose, like `EMAIL_SKIP_REASON_KEYS`: every vitest mock of
 * next-intl here returns a bare function without `.has`.
 */
export const EMAIL_FAIL_REASON_KEYS = {
  /** P — SES refused this mail for good (any other SES 4xx). */
  "ses-rejected": "sesRejected",
  /** Q — the account's daily sending quota was spent. */
  "ses-daily-quota": "sesDailyQuota",
  /** X — the SES account was suspended. */
  "ses-account-suspended": "sesAccountSuspended",
  /** S — the configuration set was still paused on the last delivery. */
  "ses-paused": "sesPaused",
  /** T — still throttled on the last delivery. */
  "ses-throttled": "sesThrottled",
  /** C — the worker's credentials were still refused on the last delivery. */
  "ses-credentials": "sesCredentials",
  /** A — 5xx, timeout or network error on the last attempt: SES may have it. */
  "ses-unknown": "sesUnknown",
  /** The claim was still held on the last delivery; the mail may have gone out. */
  "claim-held-unknown": "claimHeldUnknown",
} as const;

export type EmailFailReasonCode = keyof typeof EMAIL_FAIL_REASON_KEYS;
export type EmailFailReasonKey = (typeof EMAIL_FAIL_REASON_KEYS)[EmailFailReasonCode];

/** The locale key for a known code, `null` for anything else. */
export function emailFailReasonKey(code: string | null | undefined): EmailFailReasonKey | null {
  if (!code || !Object.prototype.hasOwnProperty.call(EMAIL_FAIL_REASON_KEYS, code)) return null;
  return EMAIL_FAIL_REASON_KEYS[code as EmailFailReasonCode];
}
